// WebSocket Service for Real-Time Communication
// Handles live updates, notifications, and real-time data across the platform

import { useAuthStore } from '@/store/auth';

// Vite env typing (local, safe)
interface ImportMetaEnv { readonly VITE_WS_URL?: string }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const importMeta: { env: ImportMetaEnv };
const VITE_ENV = (import.meta as unknown as typeof importMeta).env;

// Resolve default WS URL from env or window location
const DEFAULT_WS_URL = (() => {
  const envUrl = VITE_ENV?.VITE_WS_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim().length > 0) return envUrl.trim();
  if (typeof window !== 'undefined' && window.location) {
    const isHttps = window.location.protocol === 'https:';
    const scheme = isHttps ? 'wss' : 'ws';
    return `${scheme}://${window.location.host}/ws`;
  }
  return 'wss://skyview-assets.de/ws';
})();

interface WebSocketConfig {
  url: string;
  reconnectInterval: number;
  maxReconnectAttempts: number;
  heartbeatInterval: number;
  connectionTimeout: number;
}

interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: string;
  user_id?: string;
  room?: string;
}

interface Subscription {
  id: string;
  type: string;
  callback: (data: any) => void;
  filter?: (data: any) => boolean;
}

interface UserPresence {
  user_id: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  last_seen: string;
  page?: string;
  role?: string;
}

interface RealTimeEvent {
  // Call Center Events
  'call.incoming': { call_id: string; customer: any; agent_id?: string };
  'call.connected': { call_id: string; agent_id: string; customer_id: string };
  'call.ended': { call_id: string; duration: number; status: string };
  'call.waiting': { call_id: string; queue_position: number; wait_time: number };
  
  // Agent Events
  'agent.status_changed': { agent_id: string; status: string; previous_status: string };
  'agent.assigned': { agent_id: string; call_id: string; customer_id: string };
  'agent.available': { agent_id: string; skills: string[] };
  
  // Dashboard Events
  'dashboard.metrics_updated': { metrics: any; timestamp: string };
  'dashboard.alert': { type: string; message: string; severity: 'low' | 'medium' | 'high' | 'critical' };
  
  // User Events
  'user.notification': { title: string; message: string; type: string; action_url?: string };
  'user.presence': UserPresence;
  'user.message': { from: string; to: string; message: string; channel: string };
  
  // System Events
  'system.maintenance': { message: string; start_time: string; estimated_duration: number };
  'system.broadcast': { message: string; target_roles?: string[]; priority: string };
  
  // Investment Events
  'investment.created': { investment_id: string; user_id: string; amount: number; bank: string };
  'investment.matured': { investment_id: string; user_id: string; final_amount: number };
  'investment.alert': { user_id: string; message: string; investment_id: string };
  
  // Transaction Events
  'transaction.completed': { transaction_id: string; user_id: string; amount: number; type: string };
  'transaction.failed': { transaction_id: string; user_id: string; reason: string };
  
  // Admin Events
  'admin.user_action': { admin_id: string; action: string; target_user_id: string; details: any };
  'admin.system_change': { admin_id: string; change_type: string; details: any };
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private subscriptions: Map<string, Subscription> = new Map();
  private reconnectAttempts = 0;
  private isConnecting = false;
  private isManualClose = false;
  private heartbeatTimer?: NodeJS.Timeout;
  private reconnectTimer?: NodeJS.Timeout;
  private connectionPromise?: Promise<void>;
  private currentUser: any = null;
  private presenceTimer?: NodeJS.Timeout;

  constructor(config?: Partial<WebSocketConfig>) {
    this.config = {
      url: config?.url || DEFAULT_WS_URL,
      reconnectInterval: config?.reconnectInterval || 3000,
      maxReconnectAttempts: config?.maxReconnectAttempts || 10,
      heartbeatInterval: config?.heartbeatInterval || 30000,
      connectionTimeout: config?.connectionTimeout || 10000,
      ...config
    };

    // DON'T auto-connect - only connect manually after login
    // this.checkAuthAndConnect();
  }

  // Update configuration
  updateConfig(newConfig: Partial<WebSocketConfig>) {
    this.config = { ...this.config, ...newConfig };
  }

  // Set current user for presence tracking
  setCurrentUser(user: any) {
    this.currentUser = user;
    if (this.isConnected()) {
      this.updatePresence('online');
    }
  }

  // Check if user is authenticated and auto-connect
  private checkAuthAndConnect() {
    // Check if user is logged in (get from auth store)
    const { tokens } = useAuthStore.getState();
    if (tokens?.access_token) {
      this.connect();
    }
  }

  // Connect to WebSocket server
  async connect(forceToken?: string): Promise<void> {
    if (this.isConnecting || this.isConnected()) {
      return this.connectionPromise || Promise.resolve();
    }

    this.isConnecting = true;
    this.isManualClose = false;

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        // Get token from auth store or use provided token
        let token = forceToken;
        
        if (!token) {
          const authState = useAuthStore.getState();
          token = authState.tokens?.access_token;
          
          // Fallback to localStorage if auth store is empty
          if (!token) {
            try {
              const persistedData = localStorage.getItem('auth-storage');
              if (persistedData) {
                const parsed = JSON.parse(persistedData);
                token = parsed.state?.tokens?.access_token;
              }
            } catch (error) {
              console.error('Error parsing localStorage auth data:', error);
            }
          }
        }
        
        const wsUrl = token ? `${this.config.url}?token=${token}` : this.config.url;
        
        this.ws = new WebSocket(wsUrl);

        const connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, this.config.connectionTimeout);

        this.ws.onopen = (event) => {
          clearTimeout(connectionTimeout);
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          
          // console.log('✅ WebSocket connected'); // Removed for security
          this.emit('connected', { timestamp: new Date().toISOString() });
          
          // Start heartbeat
          this.startHeartbeat();
          
          // Update presence if user is set
          if (this.currentUser) {
            this.updatePresence('online');
            this.startPresenceTracking();
          }
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          this.isConnecting = false;
          this.stopHeartbeat();
          this.stopPresenceTracking();
          
          // console.log('❌ WebSocket disconnected:', event.code, event.reason); // Removed for security
          this.emit('disconnected', { code: event.code, reason: event.reason });

          if (!this.isManualClose && this.reconnectAttempts < this.config.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error('❌ WebSocket error:', error);
          this.emit('error', { error });
          reject(error);
        };

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  // Disconnect from WebSocket server
  disconnect() {
    this.isManualClose = true;
    this.stopHeartbeat();
    this.stopPresenceTracking();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.ws) {
      this.updatePresence('offline');
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
  }

  // Check if WebSocket is connected
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // Send message to server
  send(type: string, data: any, room?: string): boolean {
    if (!this.isConnected || !this.ws) {
      return false;
    }

    const message: WebSocketMessage = {
      type,
      data,
      timestamp: new Date().toISOString(),
      user_id: this.currentUser?.id,
      room
    };

    try {
      this.ws!.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      return false;
    }
  }

  // Subscribe to real-time events
  subscribe<K extends keyof RealTimeEvent>(
    type: K,
    callback: (data: RealTimeEvent[K]) => void,
    filter?: (data: RealTimeEvent[K]) => boolean
  ): string {
    const id = `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.subscriptions.set(id, {
      id,
      type,
      callback: callback as any,
      filter: filter as any
    });

    // Send subscription request to server
    this.send('subscribe', { event_type: type });

    return id;
  }

  // Unsubscribe from events
  unsubscribe(subscriptionId: string): boolean {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription) {
      return false;
    }

    this.subscriptions.delete(subscriptionId);
    
    // Send unsubscription request to server
    this.send('unsubscribe', { event_type: subscription.type });
    
    return true;
  }

  // Join a room for targeted messages
  joinRoom(room: string): void {
    this.send('join_room', { room });
  }

  // Leave a room
  leaveRoom(room: string): void {
    this.send('leave_room', { room });
  }

  // Update user presence
  updatePresence(status: UserPresence['status'], page?: string): void {
    if (!this.currentUser) return;

    const presence: UserPresence = {
      user_id: this.currentUser.id,
      status,
      last_seen: new Date().toISOString(),
      page: page || window.location.pathname,
      role: this.currentUser.role
    };

    this.send('presence_update', presence);
  }

  // Send notification to user(s)
  sendNotification(
    targetUserId: string | string[],
    title: string,
    message: string,
    type: string = 'info',
    actionUrl?: string
  ): void {
    this.send('notification', {
      target_user_id: targetUserId,
      title,
      message,
      type,
      action_url: actionUrl
    });
  }

  // Broadcast message to all users or specific roles
  broadcast(message: string, targetRoles?: string[], priority: string = 'normal'): void {
    this.send('broadcast', {
      message,
      target_roles: targetRoles,
      priority
    });
  }

  // Handle incoming messages
  private handleMessage(message: WebSocketMessage): void {
    // console.log('📨 WebSocket message received:', message); // Removed for security

    // Find matching subscriptions
    this.subscriptions.forEach((subscription) => {
      if (subscription.type === message.type || subscription.type === '*') {
        // Apply filter if exists
        if (!subscription.filter || subscription.filter(message.data)) {
          try {
            subscription.callback(message.data);
          } catch (error) {
            console.error('Error in subscription callback:', error);
          }
        }
      }
    });

    // Emit generic event
    this.emit(message.type, message.data);
  }

  // Schedule reconnection
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.config.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      30000
    );

    // console.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts} in ${delay}ms`); // Removed for security

    this.reconnectTimer = setTimeout(() => {
      this.connect().catch((error) => {
        console.error('Reconnection failed:', error);
      });
    }, delay);
  }

  // Start heartbeat to keep connection alive
  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected()) {
        this.send('ping', { timestamp: new Date().toISOString() });
      }
    }, this.config.heartbeatInterval);
  }

  // Stop heartbeat
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  // Start presence tracking
  private startPresenceTracking(): void {
    // Update presence every 60 seconds
    this.presenceTimer = setInterval(() => {
      this.updatePresence('online');
    }, 60000);

    // Track page changes
    window.addEventListener('beforeunload', () => {
      this.updatePresence('offline');
    });

    // Track visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.updatePresence('away');
      } else {
        this.updatePresence('online');
      }
    });
  }

  // Stop presence tracking
  private stopPresenceTracking(): void {
    if (this.presenceTimer) {
      clearInterval(this.presenceTimer);
      this.presenceTimer = undefined;
    }
  }

  // Generic event emitter
  private eventCallbacks: Map<string, Function[]> = new Map();

  on(event: string, callback: Function): void {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, []);
    }
    this.eventCallbacks.get(event)?.push(callback);
  }

  off(event: string, callback: Function): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event callback for ${event}:`, error);
        }
      });
    }
  }

  // Cleanup
  destroy(): void {
    this.disconnect();
    this.subscriptions.clear();
    this.eventCallbacks.clear();
  }
}

// Singleton instance
export const webSocketService = new WebSocketService();

// Export types
export type { 
  WebSocketConfig, 
  WebSocketMessage, 
  RealTimeEvent, 
  UserPresence, 
  Subscription 
};

// Auto-cleanup on page unload
window.addEventListener('beforeunload', () => {
  webSocketService.destroy();
});

// Auto-reconnect when user becomes online
window.addEventListener('online', () => {
  if (!webSocketService.isConnected()) {
    webSocketService.connect();
  }
});

// Disconnect when user goes offline
window.addEventListener('offline', () => {
  webSocketService.updatePresence('offline');
}); 